(() => {
  const STORAGE_KEY = 'sa:recently-viewed-products';
  const PAGE_SIZE = 50;
  const SEARCH_CHUNK = 10;
  const CARET_ICON =
    '<svg class="icon icon-caret" viewBox="0 0 10 6" aria-hidden="true"><path fill="#000000F2" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';

  const RecentlyViewedStorage = {
    getAll() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    },

    save(items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch {
        /* ignore quota errors */
      }
    },

    track({ id, handle }) {
      if (!id || !handle) return;

      const productId = String(id);
      const items = this.getAll().filter((item) => String(item.id) !== productId);

      items.unshift({
        id: productId,
        handle,
        viewedAt: Date.now(),
      });

      this.save(items);
      document.dispatchEvent(new CustomEvent('sa:recently-viewed:updated'));
    },

    getDisplayItems() {
      const currentId = document.querySelector('product-info[data-product-id]')?.dataset.productId;

      return this.getAll().filter((item) => String(item.id) !== String(currentId));
    },

    getHandles() {
      return this.getDisplayItems()
        .map((item) => item.handle)
        .filter(Boolean);
    },

    getIds() {
      return this.getDisplayItems()
        .map((item) => item.id)
        .filter(Boolean);
    },
  };

  const trackCurrentProduct = () => {
    const productInfo = document.querySelector('product-info[data-product-id][data-url]');
    if (!productInfo) return;

    const id = productInfo.dataset.productId;
    const url = productInfo.dataset.url || '';
    const handleMatch = url.match(/\/products\/([^/?#]+)/);
    const handle = handleMatch ? handleMatch[1] : null;

    if (id && handle) {
      RecentlyViewedStorage.track({ id, handle });
    }
  };

  class SaRecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.contentRoot = this.querySelector('[data-sa-recently-viewed-content]');
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.pageSize = Number(this.dataset.limit) || PAGE_SIZE;
      this.page = 1;
      this.renderToken = 0;
      this.onUpdated = () => {
        this.page = 1;
        this.render();
      };

      document.addEventListener('sa:recently-viewed:updated', this.onUpdated);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('sa:recently-viewed:updated', this.onUpdated);
    }

    getTabPanel() {
      return this.closest('[data-sa-tab-panel]');
    }

    hideSection() {
      if (this.contentRoot) {
        this.contentRoot.innerHTML = '';
      }

      const panel = this.getTabPanel();
      if (panel) {
        panel.setAttribute('data-sa-empty', 'true');
      }
    }

    showSection() {
      const panel = this.getTabPanel();
      if (panel) {
        panel.removeAttribute('data-sa-empty');
      }
    }

    async render(page = this.page || 1) {
      if (!this.contentRoot) return;

      const items = RecentlyViewedStorage.getDisplayItems();
      const isCapped = Boolean(this.dataset.limit);
      const totalPages = isCapped ? 1 : Math.max(1, Math.ceil(items.length / this.pageSize));
      const currentPage = Math.min(Math.max(1, page), totalPages);
      this.page = currentPage;

      const pageStart = isCapped ? 0 : (currentPage - 1) * this.pageSize;
      const pageItems = items.slice(pageStart, pageStart + this.pageSize);
      const ids = pageItems.map((item) => item.id).filter(Boolean);
      const handles = pageItems.map((item) => item.handle).filter(Boolean);

      // 没有最近浏览商品时，隐藏整个 Shopify section
      if (ids.length < 1) {
        this.hideSection();
        return;
      }

      const token = ++this.renderToken;

      try {
        const cards = await this.loadPageCards(ids);
        if (token !== this.renderToken) return;

        if (cards.length < 1) {
          this.hideSection();
          return;
        }

        const grid = document.createElement('div');
        grid.className = 'sa-recently-viewed__grid';
        grid.setAttribute('data-sa-recently-viewed-grid', '');
        cards.forEach((card) => grid.appendChild(card));

        this.contentRoot.replaceChildren(grid);
        this.reorderCards(handles);

        const renderedCards = this.contentRoot.querySelectorAll('[data-product-handle]');

        // 最终渲染出来没有商品卡时，继续隐藏整个 Shopify section
        if (renderedCards.length < 1) {
          this.hideSection();
          return;
        }

        if (totalPages > 1) {
          this.contentRoot.insertAdjacentHTML('beforeend', this.paginationMarkup(currentPage, totalPages));
          this.bindPagination();
        }

        this.showSection();
      } catch {
        if (token !== this.renderToken) return;
        this.hideSection();
      }
    }

    async fetchCards(ids) {
      const sectionId = this.dataset.sectionId;
      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const url = `${this.searchUrl}?sections=${encodeURIComponent(sectionId)}&type=product&q=${encodeURIComponent(query)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Unable to load recently viewed products');

      const sections = await response.json();
      const html = sections[sectionId];
      if (!html || !html.trim()) return [];

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const grid = doc.querySelector('[data-sa-recently-viewed-grid]');
      if (!grid) return [];
      return Array.from(grid.children);
    }

    async loadPageCards(ids) {
      try {
        const cards = await this.fetchCards(ids);
        if (ids.length <= SEARCH_CHUNK || cards.length >= ids.length || cards.length > SEARCH_CHUNK) {
          return cards;
        }
      } catch {
        /* fall through and load the page in smaller searches */
      }

      const chunks = [];
      for (let index = 0; index < ids.length; index += SEARCH_CHUNK) {
        chunks.push(ids.slice(index, index + SEARCH_CHUNK));
      }

      const groups = await Promise.all(chunks.map((chunk) => this.fetchCards(chunk).catch(() => [])));
      return groups.flat();
    }

    paginationMarkup(current, total) {
      const pages = [];
      if (total <= 7) {
        for (let i = 1; i <= total; i += 1) pages.push(i);
      } else {
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        pages.push(1);
        if (start > 2) pages.push('…');
        for (let i = start; i <= end; i += 1) pages.push(i);
        if (end < total - 1) pages.push('…');
        pages.push(total);
      }

      const pageButton = (value) => {
        if (value === '…') {
          return `<li class="overflow-hidden rounded-[8px] border-0"><span class="pagination__item bg-transparent text-[#000000F2]">…</span></li>`;
        }

        if (value === current) {
          return `<li class="overflow-hidden rounded-[8px] border border-black/50"><button type="button" class="pagination__item pagination__item--current bg-[#000000F2] text-[16px] text-white [&::after]:hidden" aria-current="page" disabled>${value}</button></li>`;
        }

        return `<li class="overflow-hidden rounded-[8px] border border-black/50"><button type="button" class="pagination__item link bg-transparent text-[16px] text-[#000000F2] [&::after]:hidden" data-sa-recently-viewed-page="${value}">${value}</button></li>`;
      };

      const arrow = (targetPage, direction) => {
        const enabled = targetPage >= 1 && targetPage <= total && targetPage !== current;
        const rotateClass = direction === 'prev' ? 'pagination__item--next' : 'pagination__item--prev';
        const label = direction === 'prev' ? 'Previous page' : 'Next page';

        if (!enabled) {
          return `<li class="rounded-[8px] border border-black/35"><span class="pagination__item pagination__item--disabled pagination__item-arrow">${CARET_ICON}</span></li>`;
        }

        return `<li class="rounded-[8px] border border-black/50"><button type="button" class="pagination__item ${rotateClass} pagination__item-arrow link motion-reduce" data-sa-recently-viewed-page="${targetPage}" aria-label="${label}">${CARET_ICON}</button></li>`;
      };

      return `<div class="pagination-wrapper" data-page="${current}"><nav class="pagination" role="navigation" aria-label="Pagination"><ul class="pagination__list list-unstyled justify-center gap-8 md:mt-24 md:gap-16" role="list">${arrow(current - 1, 'prev')}${pages.map(pageButton).join('')}${arrow(current + 1, 'next')}</ul></nav></div>`;
    }

    bindPagination() {
      this.contentRoot.querySelectorAll('[data-sa-recently-viewed-page]').forEach((button) => {
        button.addEventListener('click', () => {
          const nextPage = Number(button.dataset.saRecentlyViewedPage);
          if (!nextPage || nextPage === this.page) return;
          this.render(nextPage);
        });
      });
    }

    reorderCards(handles) {
      const grid = this.contentRoot.querySelector('[data-sa-recently-viewed-grid]');
      if (!grid) return;

      const cards = Array.from(grid.children);
      const ordered = [];

      handles.forEach((handle) => {
        const match = cards.find((card) => card.dataset.productHandle === handle);
        if (match) ordered.push(match);
      });

      cards.forEach((card) => {
        if (!ordered.includes(card)) ordered.push(card);
      });

      ordered.forEach((card) => grid.appendChild(card));
    }
  }

  if (!customElements.get('sa-recently-viewed')) {
    customElements.define('sa-recently-viewed', SaRecentlyViewed);
  }

  window.SaRecentlyViewedStorage = RecentlyViewedStorage;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackCurrentProduct);
  } else {
    trackCurrentProduct();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target?.querySelectorAll('sa-recently-viewed').forEach((element) => {
      element.render?.();
    });
  });
})();
